import React, { useState, useEffect } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DateInput from '../components/DateInput';

// Register ChartJS components
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function Reports() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 7 days
    endDate: new Date().toISOString().split('T')[0]
  });
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reports/tasks', {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          userId: isAdmin ? null : user.id
        }
      });

      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const taskStats = reportData?.taskStats || {};
  const serviceStats = reportData?.tasksByService || [];
  const taskTypeAmounts = reportData?.taskTypeAmounts || [];

  const pieChartData = {
    labels: ['Completed Tasks', 'Pending Tasks'],
    datasets: [{
      data: [Number(taskStats.completed || 0), Number(taskStats.pending || 0)],
      backgroundColor: ['#36A2EB', '#FF6384'],
      hoverBackgroundColor: ['#36A2EB', '#FF6384']
    }]
  };

  const barChartData = {
    labels: serviceStats.map(t => t.service),
    datasets: [{
      label: 'Tasks by Service Type',
      data: serviceStats.map(t => t.count),
      backgroundColor: '#4BC0C0'
    }]
  };

  const taskTypeAmountChartData = {
    labels: taskTypeAmounts.map(t => t.type),
    datasets: [{
      label: 'Amount by Task Type',
      data: taskTypeAmounts.map(t => t.amount),
      backgroundColor: ['#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', '#A5D6A7']
    }]
  };

  const barChartOptions = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="reports-page">
      <div className="header">
        <div className="header-content">
          <h2>Task Reports</h2>
          {/* <div className="filters">
            {isAdmin && (
              <SearchableSelect
                value={selectedUser}
                options={users.map(u => ({ value: u.id, label: u.name }))}
                onChange={(e) => setSelectedUser(e.target.value)}
                placeholder="Select user..."
                isClearable={false}
              />
            )}
            
          </div> */}
        </div>
      </div>
      <div style={{ width: '100%', height: '20px' }}>
            <div className="date-filters" style={{ float: 'right', marginLeft: "60%", display: 'flex', gap: 8 }}>
              <div style={{ minWidth: 160 }}>
                <DateInput label="From" value={dateRange.startDate} onChange={val => setDateRange({ ...dateRange, startDate: val })} />
              </div>
              <div style={{ minWidth: 160 }}>
                <DateInput label="To" value={dateRange.endDate} onChange={val => setDateRange({ ...dateRange, endDate: val })} />
              </div>
            </div>
      </div>  

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="charts-container">
        <div className="chart-card">
          <h3>Task Completion Status</h3>
          <div className="chart-wrapper">
            <Pie data={pieChartData} />
          </div>
          <div className="chart-stats">
            <div className="stat-item">
              <label>Total Tasks:</label>
              <span>{reportData?.taskStats.total || 0}</span>
            </div>
            <div className="stat-item">
              <label>Completed:</label>
              <span>{reportData?.taskStats.completed || 0}</span>
            </div>
            <div className="stat-item">
              <label>Pending:</label>
              <span>{reportData?.taskStats.pending || 0}</span>
            </div>
          </div>
        </div>

        {/* <div className="chart-card">
          <h3>Tasks by Service Type</h3>
          <div className="chart-wrapper">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div> */}

        {isAdmin && (
          <div className="chart-card">
            <h3>Task Type vs Amount</h3>
            <div className="chart-wrapper chart-with-legend">
              <div className="chart-area">
                <Bar data={taskTypeAmountChartData} options={barChartOptions} />
              </div>
              <div className="legend-list">
                {taskTypeAmounts.map((item, index) => (
                  <div className="legend-item" key={`${item.type}-${index}`}>
                    <span className="legend-color" style={{ backgroundColor: taskTypeAmountChartData.datasets[0].backgroundColor[index % taskTypeAmountChartData.datasets[0].backgroundColor.length] }} />
                    <span>{item.type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .reports-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        


        .filters {
          display: flex;
          gap: 20px;
          align-items: center;
        }

        .date-filters {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .user-select,
        .date-filters input {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .error-banner {
          background: #FFEBE6;
          color: #DE350B;
          padding: 12px 16px;
          border-radius: 3px;
          margin-bottom: 20px;
          text-align: center;
        }

        .charts-container {
          // display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin-top: 35px;
        }

        .chart-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .chart-wrapper {
          margin: 20px 0;
          height: 300px;
          display: flex;
          justify-content: center;
        }

        .chart-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 20px;
          text-align: center;
        }

        .chart-with-legend {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
        }

        .chart-area {
          flex: 1;
          min-width: 0;
        }

        .legend-list {
          min-width: 140px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding-top: 8px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #334155;
        }

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .stat-item label {
          color: #6B778C;
          font-size: 14px;
        }

        .stat-item span {
          font-size: 24px;
          font-weight: 600;
          color: #172B4D;
        }
      `}</style>
    </div>
  );
}